import { useEffect, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import debounce from 'lodash/debounce'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { EMAIL_REGEX, PHONE_REGEX } from 'src/utils/regex'
import { PATH as PATH_AUTH } from 'src/pages/auth'
import { PATH as AUTOMATIC_PLAN_PATH } from 'src/pages/automaticPlan'
import { PATH as PATH_MY_PAGE } from 'src/pages/myPage'
import { EMAIL_CHANGE, PHONE_CHANGE } from 'src/constants/contact'
import { useAppDispatch, useAppSelector } from 'src/store'
import { submitContact } from 'src/useCase/contact'
import addressRequest from 'src/api/requests/address'
import LocalStorage from 'src/utils/LocalStorage'
import {
  DuplicateAccountOptionsState,
  DuplicateType,
} from 'src/interfaces/duplicateAccount'

export type ChangeRequestFormValues = {
  name: string
  newValue: string
  contactEmail: string
}

export type Step = 'main' | 'change-value' | 'login-options'

const DUPLICATE_LABEL: Record<DuplicateType, string> = {
  email: 'メールアドレス',
  phone: '電話番号',
}

export const useDuplicateAccountOptions = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const dispatch = useAppDispatch()

  const stateFromNavigation = location.state as
    | Partial<DuplicateAccountOptionsState>
    | undefined

  const {
    type = 'email',
    value = '',
    name: knownName = '',
    contactEmail: knownContactEmail = '',
  } = stateFromNavigation || {}

  // A refresh, copied URL, or direct navigation loses the router `state` this
  // page depends on (type/value). Rather than silently rendering with an empty
  // duplicated value (which would send a CS request with a blank "現在の..."
  // line), bounce back to registration.
  const hasRequiredState = Boolean(stateFromNavigation?.value)

  // User may already have a session (registered email step created a token),
  // so they can navigate directly to My Page without logging in again.
  const hasAccessToken = Boolean(LocalStorage.accessToken)

  useEffect(() => {
    if (!hasRequiredState) {
      navigate(PATH_AUTH.REGISTER, { replace: true })
    }
  }, [hasRequiredState, navigate])

  const [step, setStep] = useState<Step>('main')
  const [isRequestSent, setIsRequestSent] = useState(false)
  const [hasDuplicateEmailPassword, setHasDuplicateEmailPassword] = useState<
    boolean | null
  >(null)
  // Keeps the submit button disabled from the moment the user types until the
  // duplicate-check API call resolves, closing the race window where a debounced
  // check had not yet fired or was still in-flight.
  const [isCheckingNewValue, setIsCheckingNewValue] = useState(false)

  const userInfo = useAppSelector((state) => state.ui.app.authInfo)
  const userHasPhone = Boolean(userInfo?.phone)

  const label = DUPLICATE_LABEL[type]

  // The Contact API requires both `name` and a valid `email` regardless of
  // contact_type. When the caller already knows them they are pre-filled and
  // hidden; otherwise the patient fills them in here.
  // For email-type duplicates, the name field is not needed (the new email
  // itself serves as the reply channel).
  const showNameField = type === 'phone' && !knownName
  const showContactEmailField = type === 'phone' && !knownContactEmail

  const validationSchema = Yup.object().shape({
    // name is only required when the field is shown (phone type with no
    // pre-filled name). For email-type duplicates the field is hidden.
    name:
      type === 'phone' && !knownName
        ? Yup.string().required('入力してください。')
        : Yup.string().notRequired(),
    newValue: Yup.string()
      // trim whitespace before validating so padding doesn't reach the API
      .transform((v: string) => v?.trim() ?? v)
      .required('入力してください。')
      .matches(
        type === 'email' ? EMAIL_REGEX : PHONE_REGEX,
        type === 'email'
          ? 'メールアドレスを正しく入力してください。'
          : '電話番号は０から始まる必要があります。',
      ),
    contactEmail:
      type === 'phone'
        ? Yup.string()
            .transform((v: string) => v?.trim() ?? v)
            .required('入力してください。')
            .matches(EMAIL_REGEX, 'メールアドレスを正しく入力してください。')
        : Yup.string().notRequired(),
  })

  const form = useForm<ChangeRequestFormValues>({
    defaultValues: {
      name: knownName,
      newValue: '',
      contactEmail: knownContactEmail,
    },
    mode: 'all',
    resolver: yupResolver(validationSchema),
  })

  const menuId = searchParams.get('menuId') || searchParams.get('menu_id')

  const goToMain = () => setStep('main')
  const goToChangeValue = () => setStep('change-value')
  // goToLoginOptions skips the change-value form, so hasDuplicateEmailPassword
  // must be fetched from the already-known duplicate `value` here.
  const goToLoginOptions = () => {
    setStep('login-options')
    checkDuplicateEmailHasPassword()
  }

  const checkDuplicateEmailHasPassword = async () => {
    if (type !== 'email' || !value) return
    try {
      await addressRequest.verifyUser({ email: value })
      setHasDuplicateEmailPassword(false)
    } catch (err: any) {
      const hp = err?.response?.data?.has_password
      if (typeof hp === 'boolean') {
        setHasDuplicateEmailPassword(hp)
      }
    }
  }

  const handleGoBack = () => navigate(-1)

  const handleNewValueBlur = async (value: string) => {
    if (!value) {
      setIsCheckingNewValue(false)
      return
    }
    const { newValue: newValueError } = form.formState.errors
    if (newValueError) {
      setIsCheckingNewValue(false)
      return
    }

    try {
      await addressRequest.verifyUser(
        type === 'email' ? { email: value } : { phone: value },
      )
      form.clearErrors('newValue')
    } catch (err: any) {
      const fieldMsg = (err?.response?.data?.errors || {})[type]?.[0]
      if (fieldMsg) {
        form.setError('newValue', { type: 'manual', message: fieldMsg })
      }
      if (type === 'email') {
        const hp = err?.response?.data?.has_password
        if (typeof hp === 'boolean') {
          setHasDuplicateEmailPassword(hp)
        }
      }
    } finally {
      setIsCheckingNewValue(false)
    }
  }

  // Debounce the duplicate-value check on every keystroke so the error/disable
  // state appears without requiring blur. debouncedCheckNewValue is memoized on
  // [type] and closes over handleNewValueBlur; this is safe because
  // handleNewValueBlur reads form.formState.errors via RHF's live proxy.
  const newValueWatch = useWatch({ control: form.control, name: 'newValue' })
  const debouncedCheckNewValue = useMemo(
    () => debounce(handleNewValueBlur, 500),
    [type],
  )
  useEffect(() => {
    // disable the submit button immediately while the debounced check is pending
    if (newValueWatch) setIsCheckingNewValue(true)
    // [SCOPE] P2 fix: trim before the debounced check so the API receives the
    // same value that Yup validates (schema uses .transform(v => v?.trim())).
    debouncedCheckNewValue(newValueWatch?.trim() ?? newValueWatch)
    return () => debouncedCheckNewValue.cancel()
  }, [newValueWatch, debouncedCheckNewValue])

  const handleSubmitChangeRequest = form.handleSubmit(async (data) => {
    const replyEmail = type === 'email' ? data.newValue : data.contactEmail
    const contactType =
      type === 'email' ? EMAIL_CHANGE.value : PHONE_CHANGE.value

    const res = await dispatch(
      submitContact({
        params: {
          id: '',
          email: replyEmail,
          name: data.name,
          contact_type: contactType,
          content: `【${label}変更依頼】\n現在の${label}: ${value}\n新しい${label}: ${data.newValue}`,
        },
      }),
    )

    if (res?.meta?.requestStatus === 'fulfilled') {
      setIsRequestSent(true)
    }
  })

  // Navigate to reset-password or setting-password based on whether the
  // duplicate email account already has a password, pre-filled with the
  // duplicate email (newValue is not filled when reached via goToLoginOptions).
  const handleGoToPasswordSetting = () => {
    const emailParam = value ? `?email=${encodeURIComponent(value)}` : ''
    if (hasDuplicateEmailPassword) {
      navigate(`${PATH_AUTH.SEND_MAIL_RESET_PASSWORD}${emailParam}`)
    } else {
      navigate(`${PATH_AUTH.SEND_MAIL_SETTING_PASSWORD}${emailParam}`)
    }
  }

  // encodeURIComponent prevents base64's '+' from being decoded as a space by
  // the URL parser, which would corrupt the redirect path.
  const loginUrl = menuId
    ? `${PATH_AUTH.SIGN_IN}?redirect=${encodeURIComponent(
        btoa(
          `${AUTOMATIC_PLAN_PATH.QUESTIONS.replace(
            ':id',
            menuId,
          )}?${searchParams.toString()}`,
        ),
      )}`
    : PATH_AUTH.SIGN_IN

  const handleGoToContact = () => {
    // [SCOPE] Fix "戻る" on Contact mis-routing to /my-page/contact-method
    // for guests reached from this duplicate-account flow. Contact's back()
    // previously branched on Redux authInfo, which is already populated for
    // a mid-registration session (e.g. phone-verification), so it can't tell
    // "logged-in customer" apart from "guest via duplicate-account". This
    // flag lets Contact route back here regardless of authInfo state.
    const email =
      type === 'email' ? value : knownContactEmail ? knownContactEmail : ''

    navigate(PATH_AUTH.CONTACT, {
      state: { ...(email ? { email } : {}), fromDuplicateAccount: true },
    })
  }

  return {
    type,
    value,
    label,
    hasRequiredState,
    hasAccessToken,
    userHasPhone,
    step,
    isRequestSent,
    hasDuplicateEmailPassword,
    isCheckingNewValue,
    form,
    showNameField,
    showContactEmailField,
    goToMain,
    goToChangeValue,
    goToLoginOptions,
    handleGoBack,
    // exposed so index.tsx can call .flush() on blur instead of re-invoking
    // handleNewValueBlur directly, preventing double API calls.
    debouncedCheckNewValue,
    handleSubmitChangeRequest,
    handleGoToPasswordSetting,
    handleGoToMyPage: () => navigate(PATH_MY_PAGE.OTHER_MENU),
    loginUrl,
    handleGoToContact,
  }
}
